/**
 * SOL → MEAL2 route quoting rules (isomorphic, pure).
 * ---------------------------------------------------------------------------
 * MEAL2 is always issued at exactly $1 per unit, so a SOL purchase is priced
 * through the live SOL/USD market rate. Because that rate moves between the
 * moment we quote and the moment the wallet signs, every quote carries:
 *
 *   • an expiry  — after it lapses the widget must re-quote before signing
 *   • a slippage tolerance — the settled rate may not drift further than this
 *
 * Both the widget (before signing) and the ledger endpoint (after settlement)
 * evaluate the same functions, so a stale or manipulated quote can never be
 * booked.
 */
export const SLIPPAGE_OPTIONS = [0.1, 0.5, 1] as const;
export type SlippageBps = number;

/** Default tolerance: 0.5 %. */
export const DEFAULT_SLIPPAGE_PCT = 0.5;
export const MIN_SLIPPAGE_PCT = 0.1;
export const MAX_SLIPPAGE_PCT = 5;

export const QUOTE_TTL_OPTIONS = [15, 30, 60] as const;
export const DEFAULT_QUOTE_TTL_S = 30;
export const MIN_QUOTE_TTL_S = 10;
export const MAX_QUOTE_TTL_S = 120;

export type SwapQuote = {
  /** SOL the user pays. */
  payAmount: number;
  /** USD per SOL used for this quote. */
  solUsd: number;
  /** USD/USDC value of the payment. */
  usdValue: number;
  /** MEAL2 units issued ($1 each). */
  receiveAmount: number;
  /** Worst acceptable MEAL2 output given the slippage tolerance. */
  minReceive: number;
  slippagePct: number;
  /** Epoch ms when the quote was produced. */
  quotedAt: number;
  /** Epoch ms after which the quote must be refreshed. */
  expiresAt: number;
  source: string;
};

export function clampSlippage(pct: number): number {
  if (!Number.isFinite(pct)) return DEFAULT_SLIPPAGE_PCT;
  return Math.min(MAX_SLIPPAGE_PCT, Math.max(MIN_SLIPPAGE_PCT, pct));
}

export function clampTtl(seconds: number): number {
  if (!Number.isFinite(seconds)) return DEFAULT_QUOTE_TTL_S;
  return Math.min(MAX_QUOTE_TTL_S, Math.max(MIN_QUOTE_TTL_S, Math.round(seconds)));
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function buildQuote(opts: {
  payAmount: number;
  solUsd: number;
  slippagePct?: number;
  ttlSeconds?: number;
  now?: number;
  source?: string;
}): SwapQuote {
  const slippagePct = clampSlippage(opts.slippagePct ?? DEFAULT_SLIPPAGE_PCT);
  const ttl = clampTtl(opts.ttlSeconds ?? DEFAULT_QUOTE_TTL_S);
  const now = opts.now ?? Date.now();
  const pay = Number.isFinite(opts.payAmount) && opts.payAmount > 0 ? opts.payAmount : 0;
  const solUsd = Number.isFinite(opts.solUsd) && opts.solUsd > 0 ? opts.solUsd : 0;
  const usdValue = round2(pay * solUsd);
  const receiveAmount = usdValue; // MEAL2 is locked to $1 per unit.

  return {
    payAmount: pay,
    solUsd,
    usdValue,
    receiveAmount,
    minReceive: round2(receiveAmount * (1 - slippagePct / 100)),
    slippagePct,
    quotedAt: now,
    expiresAt: now + ttl * 1000,
    source: opts.source ?? "jupiter",
  };
}

export function isExpired(quote: Pick<SwapQuote, "expiresAt">, now = Date.now()): boolean {
  return now >= quote.expiresAt;
}

export function secondsLeft(quote: Pick<SwapQuote, "expiresAt">, now = Date.now()): number {
  return Math.max(0, Math.ceil((quote.expiresAt - now) / 1000));
}

export type QuoteCheck = { ok: true } | { ok: false; reason: "expired" | "slippage" };

/**
 * Final gate before signing (and again after settlement): the live rate must
 * still produce at least `minReceive` MEAL2 and the quote must be fresh.
 */
export function checkQuote(
  quote: SwapQuote,
  liveSolUsd: number,
  now = Date.now(),
  graceMs = 0,
): QuoteCheck {
  if (now >= quote.expiresAt + graceMs) return { ok: false, reason: "expired" };
  const liveReceive = round2(quote.payAmount * liveSolUsd);
  if (liveReceive + 1e-9 < quote.minReceive) return { ok: false, reason: "slippage" };
  return { ok: true };
}
